import { PersonType } from "./PersonType";

/** One designer/artist/publisher as sent to the API. */
export interface GamePersonInput {
	name: string;
	type: PersonType;
}

/**
 * The form keeps designers, artists and publishers in three separate lists
 * because that is how people think about them, while the API takes a single
 * `people` array tagged by role. This is the join between the two.
 */
export const toGamePeople = (source: {
	designers?: string[];
	artists?: string[];
	publishers?: string[];
}): GamePersonInput[] => [
	...(source.designers ?? []).map((name) => ({ name, type: PersonType.Designer })),
	...(source.artists ?? []).map((name) => ({ name, type: PersonType.Artist })),
	...(source.publishers ?? []).map((name) => ({ name, type: PersonType.Publisher })),
];

/** Split an existing game's flat `people` list back into the three form lists. */
export const fromGamePeople = (people: { name: string; type: PersonType }[] | undefined) => ({
	designers: (people ?? []).filter((p) => p.type === PersonType.Designer).map((p) => p.name),
	artists: (people ?? []).filter((p) => p.type === PersonType.Artist).map((p) => p.name),
	publishers: (people ?? []).filter((p) => p.type === PersonType.Publisher).map((p) => p.name),
});

/** Wrap plain names back into the `{ id, name }` shape the Game model uses. */
export const toGameLinks = (names: string[] | undefined) =>
	(names ?? []).map((name) => ({ id: name, name }));

/**
 * Same for people. The id is synthetic - the real row id is only known once
 * the server has resolved the name - and serves purely as a React key until
 * the mutation returns the persisted game.
 */
export const toGamePersonLinks = (people: GamePersonInput[] | undefined) =>
	(people ?? []).map((person) => ({ id: `${person.type}:${person.name}`, ...person }));

/**
 * Shape a Game into what `PUT /api/game` now expects.
 *
 * The command takes categories and mechanics as plain string arrays, while the
 * Game model carries them as `{ id, name }` links. Converting here rather than
 * at each call site matters: the games table edits cells inline and PUTs the
 * whole Game object, so without this the taxonomy would arrive in the wrong
 * shape and the request would fail on a screen that never touches these fields.
 */
export const toUpdateGameCommand = (game: {
	types?: { name: string }[];
	factions?: { name: string }[];
	categories?: { name: string }[];
	mechanics?: { name: string }[];
	people?: { name: string; type: PersonType }[];
}) => ({
	...game,
	types: (game.types ?? []).map((t) => t.name),
	factions: (game.factions ?? []).map((f) => f.name),
	categories: (game.categories ?? []).map((c) => c.name),
	mechanics: (game.mechanics ?? []).map((m) => m.name),
	people: (game.people ?? []).map((p) => ({ name: p.name, type: p.type })),
});
